import { useEffect, useState } from 'react';

export type ToastTone = 'default' | 'success' | 'warning' | 'danger';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  action?: { label: string; onClick: () => void };
}

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
const listeners = new Set<Listener>();
let counter = 0;

function emit() {
  for (const l of listeners) l(toasts);
}

const AUTO_DISMISS_MS: Record<ToastTone, number | null> = {
  default: 4_000,
  success: 4_000,
  // Warnings and errors persist until dismissed. A payment failure that
  // vanishes after four seconds is a payment failure the user missed.
  warning: null,
  danger: null,
};

export function toast(input: Omit<Toast, 'id'> & { id?: string }): string {
  const id = input.id ?? `t${++counter}`;
  toasts = [...toasts, { ...input, id }];
  emit();

  const ttl = AUTO_DISMISS_MS[input.tone];
  if (ttl !== null) {
    setTimeout(() => dismissToast(id), ttl);
  }
  return id;
}

export function dismissToast(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

export function Toaster() {
  const [items, setItems] = useState<Toast[]>(toasts);

  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      data-testid="toaster"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {items.map((t) => (
        <div
          key={t.id}
          role={t.tone === 'danger' ? 'alert' : 'status'}
          aria-live={t.tone === 'danger' ? 'assertive' : 'polite'}
          className={`pointer-events-auto rounded-lg border px-4 py-3 shadow-lg ${TONE[t.tone]}`}
        >
          <p className="text-sm font-semibold">{t.title}</p>
          {t.description ? <p className="mt-1 text-xs opacity-80">{t.description}</p> : null}
          <div className="mt-2 flex items-center gap-3">
            {t.action ? (
              <button
                type="button"
                onClick={() => {
                  t.action?.onClick();
                  dismissToast(t.id);
                }}
                className="text-xs font-semibold underline underline-offset-2"
              >
                {t.action.label}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              className="ml-auto text-xs opacity-70 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

const TONE: Record<ToastTone, string> = {
  default: 'border-slate-700 bg-slate-900 text-slate-100',
  success: 'border-emerald-800 bg-emerald-950 text-emerald-100',
  warning: 'border-amber-700 bg-amber-950 text-amber-100',
  danger: 'border-red-800 bg-red-950 text-red-100',
};
